import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/characters", label: "角色介绍" },
  { to: "/settings", label: "店铺设定" },
  { to: "/story", label: "剧情世界观" },
  { to: "/faq", label: "常见问题" },
  { to: "/about", label: "关于本站" },
];

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur transition-colors ${
        scrolled ? "bg-background-50" : "bg-background-50/80"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="group flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-store-2-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold text-foreground-950">
              洞洞杂货铺
            </span>
            <span className="text-[11px] text-foreground-600">角色与设定科普站</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-100 font-bold text-primary-800"
                    : "text-foreground-700 hover:bg-background-200/60 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/characters"
            className="hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600 sm:inline-flex"
          >
            认识人物
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-200 md:hidden"
            aria-label={open ? "关闭菜单" : "打开菜单"}
            aria-expanded={open}
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {open && (
        <nav
          className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden"
          aria-label="移动端导航"
        >
          <div className="flex flex-col gap-1">
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm ${
                    isActive
                      ? "bg-primary-100 font-bold text-primary-800"
                      : "text-foreground-700 hover:bg-background-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="w-full border-t border-background-300/60 bg-background-200">
      <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-store-2-line text-lg" />
              </span>
              <span className="font-heading text-base font-bold text-foreground-950">
                洞洞杂货铺
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-foreground-600">
              《洞洞杂货店》角色与设定资料站：帮你认清店主是谁、主要人物关系，以及那间乡下杂货铺与墙上之洞背后的店铺设定。
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold text-foreground-900">资料索引</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
              <li>
                <Link to="/characters" className="hover:text-primary-700">
                  角色介绍大全
                </Link>
              </li>
              <li>
                <Link to="/settings" className="hover:text-primary-700">
                  店铺设定大全
                </Link>
              </li>
              <li>
                <Link to="/story" className="hover:text-primary-700">
                  剧情与世界观
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-primary-700">
                  常见问题
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold text-foreground-900">关于本站</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
              <li>
                <Link to="/about" className="hover:text-primary-700">
                  站点介绍
                </Link>
              </li>
              <li>
                <Link to="/about#contact" className="hover:text-primary-700">
                  留言联系
                </Link>
              </li>
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-foreground-600">
              本站为同好资料整理站，请通过漫画官方授权平台阅读正版。
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-background-300/60 pt-6 text-xs text-foreground-600 sm:flex-row">
          <p>© {new Date().getFullYear()} 洞洞杂货铺科普站 · 仅做角色与设定资料整理</p>
          <p>阅读正版，尊重创作。</p>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}